import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { find, filter } from 'lodash'
import dayjs from 'dayjs'

import Operations from '../../../../operations'
import Selectors from '../../../../selectors'
import {
  ChartLogList,
  SectionHeader,
  EmptyState,
} from '../../../../components/generic'
import StockListItem from '../ResidentEmarScreens/EMARResidentStockScreen/StockListItem'
import { EMARMedicationScreenDetailsSection } from '../ResidentEmarScreens/components'
import { withRouter } from '../../../../withRouter'
import useQuery from '../../../../hooks/useQuery'

const renderStock = (props) => {
  const { medication, resident, stockItem } = props
  return (
    <div style={styles.stockContainer}>
      <StockListItem
        label="Stock"
        icon={stockItem && stockItem.icon}
        medication={medication}
        log={medication.stockLog}
        residentId={resident.id}
      />
    </div>
  )
}

// Medication Details section
const renderMedicationDetailsSection = (props) => {
  const {
    medication,
    emarStockEnabled,
    emarRoundTimings,
    isPDF,
    resident,
    stateProps,
    user,
  } = props

  return (
    <EMARMedicationScreenDetailsSection
      emarStockEnabled={emarStockEnabled}
      medication={medication}
      emarRoundTimings={emarRoundTimings}
      isPDF={isPDF}
      resident={resident}
      user={user}
      {...stateProps}
    />
  )
}

const renderLogsEmptyState = () => {
  const helpLink =
    'https://support.logmycare.co.uk/en/articles/4851799-how-do-i-sign-scheduled-medication-in-the-carer-app'
  const imageSrc =
    'https://lmc-data-production.s3.eu-west-2.amazonaws.com/icons/happy_logan.png'

  return (
    <EmptyState imageSrc={imageSrc} title="No Logs">
      <div>There are no logs relating to this medication yet.</div>
      <div>
        Once you’ve signed off a MAR chart or added a comment in the Carer App,
        they'll appear here.
      </div>
      <div>
        Learn more about signing medications{' '}
        <a href={helpLink} target="_blank" rel="noreferrer">
          here
        </a>
        .
      </div>
    </EmptyState>
  )
}

const renderLogsList = (props) => {
  const { logs } = props

  return (
    <div style={styles.logListContainer}>
      <ChartLogList logs={logs} style={styles.logList} />
    </div>
  )
}

const renderMedicationLogs = (props) => {
  const { logs, isPDF } = props

  return (
    <div style={isPDF ? styles.pageBreak : styles.logsSectionContainer}>
      <SectionHeader
        greyLine
        isSubSection
        title="Logs"
        titleStyles={{ fontWeight: 400 }}
      />
      {!logs.length ? renderLogsEmptyState() : renderLogsList(props)}
    </div>
  )
}

/**
@description Renders screen for an individual medication
@param {array} emarRoundTimings. The different rounds for that home e.g. 14:00, 19:00 etc.
Not all medications are administered at every round.
@param {array} logs. The logs for the medication
@param {object} medication. The medication.
@param {function} openDiscontinueMedModal. Opens the DiscontinueMed modal.
@param {object} resident. The resident.
@returns {function} ChartScreen
*/

export function ResidentMedChartScreen(props) {
  const {
    emarStockEnabled,
    fetchData,
    fetchLogs,
    logs,
    medication: { deleted, deletedOn },
    router: { params },
  } = props

  // PDF export works by rendering the screen with search params
  // Eg. /residents/602fc3830ba1fe8d6e4a7473/pdf/emar?printpdf=1&startDate=...
  const query = useQuery()
  const isPDF = query.get('printpdf')
  const startDateQuery = query.get('startDate')
  const endDateQuery = query.get('endDate')

  let defaultEndDate
  let defaultStartDate

  if (startDateQuery) defaultStartDate = dayjs(startDateQuery)
  else if (deleted && deletedOn)
    defaultStartDate = dayjs(deletedOn).subtract(14, 'days')
  else defaultStartDate = dayjs().subtract(14, 'days')

  if (endDateQuery) defaultEndDate = dayjs(endDateQuery)
  else if (deleted && deletedOn) defaultEndDate = dayjs(deletedOn)
  else defaultEndDate = dayjs()

  const [startDate, setStartDate] = React.useState(defaultStartDate)
  const [endDate, setEndDate] = React.useState(defaultEndDate)
  const [viewMedDetails, setViewMedDetails] = React.useState(true)

  const medicationId = params.medication_id

  const residentId = params.id

  useEffect(() => {
    if (medicationId && residentId) {
      fetchData({
        medicationId,
        residentId,
        startDate,
        endDate,
      })
    }
    if (medicationId) {
      fetchLogs(medicationId)
    }
  }, [startDate, endDate, medicationId, residentId])

  const stateProps = {
    endDate,
    startDate,
    setEndDate,
    setStartDate,
    setViewMedDetails,
    viewMedDetails,
  }

  if (!logs) return null

  return (
    <div>
      {emarStockEnabled && renderStock(props)}
      {renderMedicationDetailsSection({ ...props, stateProps, isPDF })}
      {renderMedicationLogs({ ...props, isPDF })}
    </div>
  )
}

ResidentMedChartScreen.propTypes = {
  emarRoundTimings: PropTypes.array.isRequired,
  emarStockEnabled: PropTypes.array.isRequired,
  fetchLogs: PropTypes.func.isRequired,
  logs: PropTypes.array.isRequired,
  medication: PropTypes.object.isRequired,
  resident: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
}

const styles = {
  logList: {
    marginLeft: 5,
    marginTop: 40,
  },
  logsSectionContainer: {
    paddingTop: 24,
  },
  logListContainer: {
    display: 'flex',
  },
  medRoundIcon: {
    margin: '-2px 4px 0px -4px',
    maxHeight: 20,
  },
  pageBreak: {
    pageBreakAfter: 'always',
    pageBreakInside: 'avoid',
    paddingTop: 38,
  },
  stockContainer: {
    marginTop: 16,
  },
}

const mapStateToProps = (state, props) => ({
  emarStockEnabled: state.global.home.features?.eMARStock?.enabled,
  emarRoundTimings: state.data['emar-round-timings'],
  logs: filter(Selectors.populateLogs(state), {
    emarResidentMedication: props.router.params.medication_id,
  }),
  medication: Selectors.getMedicationMarChart(state, props),
  stockItem: find(state.data['log-category-items'], {
    itemId: 'emarStockCount',
  }),
  resident: Selectors.getResidentProfile(state, { id: props.router.params.id }),
  user: Selectors.getCurrentUserProfile(state),
})

const mapDispatchToProps = (dispatch) => ({
  fetchData: ({ medicationId, residentId, startDate, endDate }) => {
    dispatch(Operations.loadList('emar-round-timings'))
    dispatch(
      Operations.loadList('emar-rounds', {
        resident: residentId,
        from: dayjs(startDate).startOf('day').toISOString(),
        to: dayjs(endDate).endOf('day').toISOString(),
      })
    )
    dispatch(Operations.loadListItem('emar-resident-medications', medicationId))
  },
  fetchLogs: (medicationId) =>
    dispatch(
      Operations.loadList('logs', { emarResidentMedication: medicationId })
    ),
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ResidentMedChartScreen)
)
